import { defineComponent as _defineComponent } from 'vue'
import { normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderSlot as _renderSlot, unref as _unref, createElementBlock as _createElementBlock, mergeProps as _mergeProps, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode, Teleport as _Teleport } from "vue"

const _hoisted_1 = ["data-test", "aria-labelledby"]
const _hoisted_2 = { class: "ds-modal-dialog__container" }
const _hoisted_3 = {
  key: 4,
  class: "ds-modal-dialog__sticky-bottom"
}

import { provide, useSlots, computed, watch, onBeforeUnmount, ref } from "vue";
// ISSUE: Competing focus traps
// FIXME: Resolve in future
// import { UseFocusTrap } from "@vueuse/integrations/useFocusTrap/component";
import { isModalVisibleInjectionKey } from "./ds-modal-dialog-injection-key";
import DsDivider from "../divider/ds-divider.vue";
import DsButton from "../button/ds-button.vue";

import { DsModalDialogExposeType, ModalPosition } from "./ds-modal-dialog-models";
import { Theme } from "../types/theme";
import { useStackedModals } from "@sazka/utils/composables/use-stacked-modals";


export default /*@__PURE__*/_defineComponent({
  __name: 'ds-modal-dialog',
  props: {
    dialogLabelId: {},
    modelValue: { type: Boolean },
    modalPosition: { default: "top" },
    topDividerVisible: { type: Boolean, default: true },
    bottomDividerVisible: { type: Boolean, default: true },
    theme: { default: "light" },
    contentScrollable: { type: Boolean, default: false },
    stickyBottom: { type: Boolean },
    isInStorybook: { type: Boolean, default: false },
    dataTest: {},
    hasDialogCloseBtn: { type: Boolean, default: true },
    closeOnClickOutside: { type: Boolean, default: true },
    animate: { type: Boolean, default: false },
    preventClose: { type: Boolean, default: false },
    cssClass: { default: "" }
  },
  emits: ["update:modelValue", "modal-closed"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const dialogBodyRef = ref<HTMLElement>(null);
const contentRef = ref<HTMLElement>();

const slots = useSlots();
const props = __props;

const emit = __emit;

// dont use defineModal macro. If you have v-if on the parent, it doesnt remove from StackedModals.
// DefineModal can use later maybe, its too much new feature.
const modelValueInternal = computed({
    get() {
        return props.modelValue;
    },
    set(newValue) {
        emit("update:modelValue", newValue);
    },
});

/**
 * Handles showing / hiding modal
 */
provide(isModalVisibleInjectionKey, modelValueInternal);

const closeDialogInternal = () => {
    if (props.preventClose) return;

    modelValueInternal.value = false;
    emit("modal-closed");
};

const keyDownEventHandler = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
        closeDialogInternal();
    }
};

const { addToStackedModals, removeFromStackedModals } = useStackedModals();

const dialogIsOpening = async () => {
    window.addEventListener("keydown", keyDownEventHandler);
    addToStackedModals();
};

const dialogIsClosing = () => {
    window.removeEventListener("keydown", keyDownEventHandler);
    removeFromStackedModals();
};

watch(
    modelValueInternal,
    (newModelValue, oldModelValue) => {
        if (newModelValue && !oldModelValue) {
            dialogIsOpening();
        } else if (!newModelValue && oldModelValue) {
            dialogIsClosing();
        }
    },
    {
        immediate: true,
    }
);

onBeforeUnmount(() => {
    if (modelValueInternal.value) {
        dialogIsClosing();
    }
});

const backgroundClass = computed(() => {
    if (Array.isArray(props.cssClass)) {
        return props.cssClass.filter(Boolean).map((cssClass) => `${cssClass}__background`);
    }
    return props.cssClass ? `${props.cssClass}__background` : undefined;
});

const bodyClass = computed(() => {
    if (Array.isArray(props.cssClass)) {
        return props.cssClass.filter(Boolean).map((cssClass) => `${cssClass}__body`);
    }
    return props.cssClass ? `${props.cssClass}__body` : undefined;
});

const scrollContent = (topPosition = 0, smoth = false) => {
    if (!props.contentScrollable || !contentRef.value) {
        return;
    }

    contentRef.value.scroll({ top: topPosition, behavior: smoth ? "smooth" : "instant" });
};

__expose<DsModalDialogExposeType>({
    dialogBodyRef,
    scrollContent,
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
    _createVNode(_Transition, {
      name: _ctx.animate ? 'ds-modal-dialog-animation' : undefined,
      duration: _ctx.animate ? 250 : undefined
    }, {
      default: _withCtx(() => [
        (modelValueInternal.value)
          ? (_openBlock(), _createElementBlock("div", _mergeProps({
              key: 0,
              class: ["ds-modal-dialog", [
                    _ctx.cssClass,
                    _ctx.modalPosition,
                    {
                        'is-dark': _ctx.theme === 'dark',
                        'ds-modal-dialog--content-scroll': _ctx.contentScrollable || _ctx.stickyBottom,
                        'ds-modal-dialog--sticky-bottom': _ctx.stickyBottom,
                    },
                ]]
            }, _ctx.$attrs), [
              _createElementVNode("div", {
                class: _normalizeClass(["ds-modal-dialog__background", backgroundClass.value]),
                onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.closeOnClickOutside ? closeDialogInternal() : null))
              }, null, 2),
              _createElementVNode("div", {
                class: _normalizeClass(["ds-modal-dialog__body", bodyClass.value]),
                role: "dialog",
                "data-test": _ctx.dataTest,
                "aria-modal": "true",
                "aria-labelledby": _ctx.dialogLabelId,
                ref_key: "dialogBodyRef",
                ref: dialogBodyRef
              }, [
                (_ctx.hasDialogCloseBtn)
                  ? (_openBlock(), _createBlock(DsButton, {
                      key: 0,
                      type: "primary",
                      theme: _ctx.theme,
                      class: "ds-modal-dialog__close",
                      "aria-label": "Zavřít",
                      "icon-left": "cross",
                      onClick: _cache[1] || (_cache[1] = ($event: any) => (closeDialogInternal())),
                      "data-test": "ds-modal-close",
                      "is-in-story-book": props.isInStorybook
                    }, null, 8, ["theme", "is-in-story-book"]))
                  : _createCommentVNode("", true),
                _createElementVNode("div", _hoisted_2, [
                  _renderSlot(_ctx.$slots, "top"),
                  (_ctx.topDividerVisible && _unref(slots).top && (_unref(slots).content || _unref(slots).bottom))
                    ? (_openBlock(), _createBlock(DsDivider, {
                        key: 0,
                        class: "ds-modal-dialog__top-divider",
                        theme: _ctx.theme
                      }, null, 8, ["theme"]))
                    : _createCommentVNode("", true),
                  (!!_unref(slots).content)
                    ? (_openBlock(), _createElementBlock("div", {
                        key: 1,
                        class: "ds-modal-dialog__content",
                        ref_key: "contentRef",
                        ref: contentRef
                      }, [
                        _renderSlot(_ctx.$slots, "content")
                      ], 512))
                    : _createCommentVNode("", true),
                  (_ctx.bottomDividerVisible && (_unref(slots).top || _unref(slots).content) && _unref(slots).bottom)
                    ? (_openBlock(), _createBlock(DsDivider, {
                        key: 2,
                        class: "ds-modal-dialog__bottom-divider",
                        theme: _ctx.theme
                      }, null, 8, ["theme"]))
                    : _createCommentVNode("", true),
                  (!_ctx.stickyBottom)
                    ? _renderSlot(_ctx.$slots, "bottom", { key: 3 })
                    : (_unref(slots).bottom)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                          _renderSlot(_ctx.$slots, "bottom")
                        ]))
                      : _createCommentVNode("", true)
                ])
              ], 10, _hoisted_1)
            ], 16))
          : _createCommentVNode("", true)
      ]),
      _: 3
    }, 8, ["name", "duration"])
  ]))
}
}

})